/**
 * Activity log API service
 */
import api from '@/lib/api';
import { MOCK_ACTIVITY_LOG, getActionLabel as mockGetActionLabel, addActivityLogEntry as mockAddEntry } from '@/data/mockActivityLog';
import type { AuditLog } from '@/types/models';

const USE_MOCK = !import.meta.env.VITE_API_URL || import.meta.env.VITE_USE_MOCK === 'true';

function delay<T>(val: T, ms = 250): Promise<T> {
  return new Promise((r) => setTimeout(() => r(val), ms));
}

function unwrapCollection<T>(payload: unknown): T[] {
  if (Array.isArray(payload)) return payload;
  if (payload && typeof payload === 'object') {
    const obj = payload as Record<string, unknown>;
    if (Array.isArray(obj.data)) return obj.data as T[];
  }
  return [];
}

export async function fetchActivityLog(entityType?: string, entityId?: string): Promise<AuditLog[]> {
  if (USE_MOCK) {
    let logs = [...MOCK_ACTIVITY_LOG];
    if (entityType && entityId) {
      logs = logs.filter((l) => l.entity_type === entityType && l.entity_id === entityId);
    }
    return delay(logs.sort((a, b) => new Date(b.timestamp).getTime() - new Date(a.timestamp).getTime()));
  }
  const params = new URLSearchParams();
  if (entityType) params.set('entity_type', entityType);
  if (entityId) params.set('entity_id', entityId);
  const res = await api.get(`/api/activity-log?${params}`);
  return unwrapCollection<AuditLog>(res.data);
}

export async function fetchWorkOrderActivity(workOrderId: string, assetIds: string[] = []): Promise<AuditLog[]> {
  if (USE_MOCK) {
    const logs = MOCK_ACTIVITY_LOG.filter(
      (l) =>
        (l.entity_type === 'work_order' && l.entity_id === workOrderId) ||
        (l.entity_type === 'asset' && assetIds.includes(l.entity_id))
    ).sort((a, b) => new Date(b.timestamp).getTime() - new Date(a.timestamp).getTime());
    return delay(logs.slice(0, 20));
  }
  const res = await api.get(`/api/work-orders/${workOrderId}/activity`);
  return unwrapCollection<AuditLog>(res.data);
}

export { mockGetActionLabel as getActionLabel };
